import { useAtom } from 'jotai';
import { z } from 'zod';

import { addressSchema, createLog, LogSeverity } from '@ekhodealer/ekho-common';
import { Box, Form, Stack } from '@ekhodealer/ekho-common/rev';

import type { Address } from '@ekhodealer/ekho-common';

import { useCurrentServicesProvider } from '@common/hooks/useCurrentServicesProvider';

import { useUpdateBusinessDetails } from '../../api/configuration/business-details/useUpdateBusinessDetails';
import { forceValidateBusinessAddressesAtom } from '../../atoms/onboardingValidationAtoms';

/** Address fields that exist on the underlying data but not on the base ServicesProvider class. */
interface AddressFields {
	legalAddress?: Address | null;
	physicalAddress?: Address | null;
}

/**
 * Helper function to get initial form data from ServicesProvider
 * Only works with service provider types that have both legalAddress and physicalAddress.
 * These fields live on the underlying data object, not on the base ServicesProvider class.
 */
function getDefaultValues(servicesProvider: { toObject?: () => Record<string, unknown> }): {
	legalAddress?: Address | null;
	physicalAddress?: Address | null;
} {
	const data = (servicesProvider.toObject?.() ?? servicesProvider) as unknown as AddressFields;
	return {
		legalAddress: data.legalAddress ?? null,
		physicalAddress: data.physicalAddress ?? null,
	};
}

/**
 * Helper function to get update payload from form data
 * Only works with service provider types that have both legalAddress and physicalAddress
 */
function getServicesProviderFromFormData(
	data: { legalAddress?: Address | null; physicalAddress?: Address | null },
	currentServicesProvider: { toObject?: () => Record<string, unknown> },
): Record<string, unknown> {
	const currentData = (currentServicesProvider.toObject?.() ?? currentServicesProvider) as unknown as AddressFields;
	return {
		legalAddress: data.legalAddress ?? currentData.legalAddress ?? undefined,
		physicalAddress: data.physicalAddress ?? currentData.physicalAddress ?? undefined,
	};
}

const legalAddressSchema = z.object({
	legalAddress: addressSchema.nullable().optional(),
});

const physicalAddressSchema = z.object({
	physicalAddress: addressSchema.nullable().optional(),
});

type LegalAddressFormData = z.infer<typeof legalAddressSchema>;
type PhysicalAddressFormData = z.infer<typeof physicalAddressSchema>;

export function BusinessAddressesForm() {
	const { data: servicesProvider } = useCurrentServicesProvider();
	const updateBusinessDetailsMutation = useUpdateBusinessDetails();
	const [forceValidation, setForceValidation] = useAtom(forceValidateBusinessAddressesAtom);

	if (!servicesProvider) return null;

	const defaults = getDefaultValues(servicesProvider);

	const handleLegalSubmit = async (
		data: LegalAddressFormData,
	): Promise<{ isSuccess: boolean }> => {
		try {
			const updatedServicesProvider = getServicesProviderFromFormData(
				{ legalAddress: data.legalAddress },
				servicesProvider,
			);
			await updateBusinessDetailsMutation.mutateAsync({
				servicesProvider: updatedServicesProvider,
			});
			setForceValidation(false);
			return { isSuccess: true };
		} catch (error) {
			createLog(
				'config-mgmt',
				`Failed to update legal address: ${
					error instanceof Error ? error.message : String(error)
				}`,
				LogSeverity.ERROR,
			);
			return { isSuccess: false };
		}
	};

	const handlePhysicalSubmit = async (
		data: PhysicalAddressFormData,
	): Promise<{ isSuccess: boolean }> => {
		try {
			const updatedServicesProvider = getServicesProviderFromFormData(
				{ physicalAddress: data.physicalAddress },
				servicesProvider,
			);
			await updateBusinessDetailsMutation.mutateAsync({
				servicesProvider: updatedServicesProvider,
			});
			setForceValidation(false);
			return { isSuccess: true };
		} catch (error) {
			createLog(
				'config-mgmt',
				`Failed to update physical address: ${
					error instanceof Error ? error.message : String(error)
				}`,
				LogSeverity.ERROR,
			);
			return { isSuccess: false };
		}
	};

	return (
		<Stack itemsSpacing='16'>
			<Box padding='24' background='secondary' rounding='12' showOverflow>
				<Form
					name='business-legal-address'
					schema={legalAddressSchema}
					defaultValues={{ legalAddress: defaults.legalAddress }}
					onSubmit={handleLegalSubmit}
					forceValidation={forceValidation}
					title='Legal address'
					titleLevel='subgroup'
					titleWeight='semibold'
					showSaveAndClear>
					<Form.Field.Address
						schemaField='legalAddress'
						labelText='Address'
						placeholder='Start typing address'
						size='large'
					/>
				</Form>
			</Box>
			<Box padding='24' background='secondary' rounding='12' showOverflow>
				<Form
					name='business-physical-address'
					schema={physicalAddressSchema}
					defaultValues={{ physicalAddress: defaults.physicalAddress }}
					onSubmit={handlePhysicalSubmit}
					forceValidation={forceValidation}
					title='Physical address'
					titleLevel='subgroup'
					titleWeight='semibold'
					showSaveAndClear>
					<Form.Field.Address
						schemaField='physicalAddress'
						labelText='Address'
						placeholder='Start typing address'
						size='large'
					/>
				</Form>
			</Box>
		</Stack>
	);
}

export default BusinessAddressesForm;
